import { ReactNode } from 'react';
import { Button, ButtonLink, Flex, Text } from '@backstage/ui';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './notFound.module.css';

/**
 * Renders the path the user actually asked for, trimmed so a pasted link with
 * a long query string cannot blow the terminal panel out of its column.
 */
const useAttemptedPath = () => {
  const { pathname, search } = useLocation();
  const path = `${pathname}${search}`;
  return path.length > 64 ? `${path.slice(0, 63)}…` : path;
};

/**
 * The app-wide 404, replacing the stock `ErrorPage` from core-components.
 *
 * Framed as a shell session because that is the language the rest of the app
 * already speaks - monospace counters on the home page, the console palette,
 * the "DEV" mark. It also lets the page do something the generic illustration
 * could not: show the URL that failed, which is the one piece of information
 * a user needs to spot their own typo.
 */
export const NotFoundPage = ({ children }: { children?: ReactNode }) => {
  const navigate = useNavigate();
  const path = useAttemptedPath();

  return (
    <div className={styles.root}>
      <div className={styles.panel}>
        <div className={styles.titleBar}>
          <span className={styles.dots} aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          <span className={styles.titleBarText}>developing — 404</span>
        </div>

        <div className={styles.terminal}>
          <p className={styles.line}>
            <span className={styles.prompt} aria-hidden="true">
              $
            </span>
            <span>open {path}</span>
          </p>
          <p className={styles.output}>open: no such page</p>
          <p className={styles.line} aria-hidden="true">
            <span className={styles.prompt}>$</span>
            <span className={styles.cursor} />
          </p>
        </div>
      </div>

      <div className={styles.copy}>
        <Text as="h1" variant="title-small" weight="bold">
          This page doesn't exist
        </Text>
        <Text variant="body-medium" color="secondary" className={styles.blurb}>
          The link may be out of date, or the page may have moved. Search the
          portal if you are looking for a specific component or doc.
        </Text>
      </div>

      <Flex gap="3" align="center" className={styles.actions}>
        <ButtonLink href="/" variant="primary" size="medium">
          Back to home
        </ButtonLink>
        <Button variant="secondary" size="medium" onClick={() => navigate(-1)}>
          Go back
        </Button>
        <ButtonLink href="/search" variant="tertiary" size="medium">
          Search
        </ButtonLink>
      </Flex>

      {children}
    </div>
  );
};
